import { Card, Typography } from '@mui/material';
import React from 'react';

import { TITLE_MAP } from './CsaScoreCard';

export const EXPLANATION_MAP = {
    unsafe_driving_score: [
        'This score measures a fleet’s history of dangerous driving behaviors, such as speeding, reckless driving, and failure to wear a seat belt.',
        'Violations are weighted by severity, with recent infractions impacting the score more heavily.',
        'A high score can lead to increased DOT inspections, higher insurance costs, and regulatory intervention.',
    ],
    crash_indicator_score: [
        'This score measures a fleet’s history of reportable crashes, using data from police reports and FMCSA records',
        'It considers crash frequency and severity, with recent incidents having a greater impact on the score.',
        'A high score can lead to increased DOT inspections, higher insurance costs, and regulatory intervention.',
    ],
    hos_compliance_score: [
        'This score measures a fleet’s adherence to federal driving time regulations designed to prevent fatigue-related accidents.',
        'Violations like exceeding allowed driving hours, falsifying logs, or failing to use an electronic logging device (ELD) increase the score.',
        'A high score can result in fines, audits, and intervention.',
    ],
    vehicle_maintenance_score: [
        'This score measures a fleet’s record of properly maintaining commercial vehicles to prevent mechanical failures.',
        'Violations such as brake issues, worn tires, faulty lights, and improper load securement increase the score.',
        'A high score can lead to more roadside inspections and penalties.',
    ],
    controlled_substances_alcohol_score: [
        'This score measures a fleet’s compliance with regulations prohibiting drug and alcohol use by drivers.',
        'Violations like positive drug or alcohol tests, possession of prohibited substances, or refusal to test increase the score.',
        'A high score can lead to stricter monitoring and penalties.',
    ],
    hazardous_materials_score: [
        'This score measures a fleet’s adherence to regulations for transporting hazardous materials safely.',
        'Violations such as improper packaging, labeling, placarding, or failing to secure hazardous cargo increase the score.',
        'A high score can lead to fines, shipping restrictions, and increased inspections.',
    ],
    driver_fitness_score: [
        'This score measures a fleet’s compliance with regulations ensuring drivers are qualified to operate commercial vehicles.',
        'Violations such as invalid CDLs, medical certification issues, or lack of proper training increase the score.',
        'A high score can result in fines and restrictions.',
    ],
    iss_score: [
        'This score is used by the FMCSA to determine how often a carrier should be subject to roadside inspections.',
        'It is based on CSA scores above, with higher scores leading to more frequent inspections.',
        'You can improve your ISS score by maintaining low CSA scores, addressing violations promptly, and ensuring compliance with safety regulations',
    ],
};

interface CsaScoreExplanationCardProps {
    type: string;
}

const CsaScoreExplanationCard: React.FC<CsaScoreExplanationCardProps> = (props: CsaScoreExplanationCardProps) => {
    return (
        <Card
            id={`${props.type.replaceAll('_', '-')}-explanation-card`}
            data-testid={`${props.type.replaceAll('_', '-')}-explanation-card`}
            sx={{ width: '275px', height: '300px', p: 2, flexShrink: 0 }}
        >
            <Typography variant="overline" sx={{ lineHeight: 1, mb: 1 }}>
                {TITLE_MAP[props.type as keyof typeof TITLE_MAP]}
            </Typography>
            {EXPLANATION_MAP[props.type as keyof typeof EXPLANATION_MAP].map((explanation, index) => {
                return (
                    <Typography key={index} variant="body2" sx={{ my: 1 }}>
                        {explanation}
                    </Typography>
                );
            })}
        </Card>
    );
};

export default CsaScoreExplanationCard;
